import { Product } from "@/types"
import { SerpElement } from "@nosto/search-js/preact/serp"
import styles from "./Product.module.css"
import skeleton from "./skeleton.module.css"
import "./DynamicCardProduct.css"
import DynamicCard from "@/elements/DynamicCard/DynamicCard"
import { cl } from "@nosto/search-js/utils"
import { getVariantId } from "@/utils/getVariantId"

type Props = {
  product: Product
}

/**
 * PLP / category / search-result grid card.
 *
 * Renders the Dickies product card by fetching the Shopify-rendered section
 * `nosto-dickies-product-card` via Nosto's `<nosto-dynamic-card>` web component,
 * so all family-swatch, PFAS, badge, price, clearance, discount-message, and
 * quick-view behaviour stays owned by Liquid (see Agent C's theme foundation).
 *
 * The skeleton path (driven by `skeletonContent` putting `tags1: ["skeleton"]`
 * on placeholder hits) is preserved so the initial pre-search layout still
 * shows a skeleton without trying to fetch an unknown product handle.
 */
export default function DynamicCardProduct({ product }: Props) {
  const isSkeleton = product.tags1?.includes("skeleton")

  if (isSkeleton) {
    return (
      <div className={cl(styles.container, skeleton.skeleton)} aria-label={`Product ${product.name}`}>
        <div className={styles.image}>
          <img src={product.imageUrl} alt={product.name} className={styles.img} />
        </div>
        <div className={styles.info}>
          <div className={skeleton.text}>...</div>
          <div className={skeleton.text}>...</div>
          <div className={skeleton.text}>...</div>
        </div>
      </div>
    )
  }

  // Without a handle the section URL `/products/{handle}?section_id=...` cannot
  // be built, so skip rendering instead of issuing a guaranteed-404 fetch.
  if (!product.handle) {
    return null
  }

  const variantId = getVariantId(product)

  return (
    <SerpElement
      as={DynamicCard}
      hit={{
        productId: product.productId!,
        url: product.url
      }}
      componentProps={{
        handle: product.handle,
        section: "nosto-dickies-product-card",
        placeholder: true,
        lazy: false,
        className: styles.container,
        ...(variantId ? { "variant-id": variantId } : {})
      }}
    />
  )
}
